import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useCustomFormsById } from 'lib/hooks/queries/customForms/useCustomFormsById';

export function cloneFormSafely(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export const useAdvancedFormData = ({
  currAdvForm,
  queryKey,
  isMacro,
  activeViewType,
  advancedNoteRef,
  isScrolling,
  updateMacro,
  updateForm
}) => {
  const { id, appointmentId } = useParams();
  const queryClient = useQueryClient();

  const formId = currAdvForm.form_id || currAdvForm.id;

  const cachedData = queryClient.getQueryData(queryKey);
  const initialForm = cachedData?.data?.formResponse
    ? cloneFormSafely(cachedData.data.formResponse)
    : null;

  const [currentForm, setCurrentForm] = useState(initialForm);
  const [oldForm, setOldForm] = useState(initialForm);
  const [macroVersion, setMacroVersion] = useState(0);

  useEffect(() => {
    if (isMacro && currAdvForm?.id) {
      const clonedForm = cloneFormSafely(currAdvForm);
      setCurrentForm(clonedForm);
      setOldForm(cloneFormSafely(clonedForm));
    }
  }, [isMacro, currAdvForm]);

  useEffect(() => {
    if (!isMacro && currAdvForm?.is_macro && (currAdvForm?.form_id || currAdvForm?.id)) {
      const formCopy = cloneFormSafely(currAdvForm);
      delete formCopy.is_macro;
      setCurrentForm(formCopy);
      setOldForm(cloneFormSafely(formCopy));
      setMacroVersion((v) => v + 1);
    }
  }, [currAdvForm?.is_macro]);

  useEffect(() => {
    return () => {
      if (activeViewType === 'multiple' && advancedNoteRef?.current && formId) {
        try {
          if (advancedNoteRef.current && typeof advancedNoteRef.current === 'object') {
            delete advancedNoteRef.current[formId];
          }
        } catch (err) {
          /* ignore */
        }
      }
    };
  }, [activeViewType, formId, advancedNoteRef]);

  const { isLoading, isFetching, isFetched } = useCustomFormsById({
    dependencies: [
      formId,
      currAdvForm?.created_at || currAdvForm?.updated_at,
      appointmentId,
      id,
      isMacro
    ],
    params: {
      formId,
      appointmentId,
      patientId: id,
      patientResponses: false
    },
    options: {
      enabled: !currentForm && !!(currAdvForm.form_id || currAdvForm?.id) && !isScrolling,
      onSuccess: ({ data }) => {
        if (data?.formResponse?.id) {
          const keepCurrAdvForm = currAdvForm?.is_macro || (isMacro && !!currAdvForm?.json);

          const formToUse = keepCurrAdvForm ? { ...currAdvForm } : data?.formResponse;

          if (formToUse && 'is_macro' in formToUse) {
            delete formToUse.is_macro;
          }

          updateForm(formToUse);

          if (!keepCurrAdvForm && updateMacro) updateMacro(formToUse);
        }
      }
    }
  });

  return {
    currentForm,
    setCurrentForm,
    oldForm,
    setOldForm,
    isLoading,
    isFetching,
    isFetched,
    macroVersion
  };
};
